import React, { useRef, useState, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// 10 Real Stores in Gujarat with Correct Coordinates
const LOCATIONS = [
  {
    id: 1,
    name: "Rajkot Store",
    address: "Atika Industrial Area, Dhebar Road, Rajkot",
    coords: [70.8022, 22.3039], // Rajkot city center
  },
  {
    id: 2,
    name: "Ahmedabad Store",
    address: "SG Highway, Ahmedabad",
    coords: [72.5714, 23.0225], // Ahmedabad
  },
  {
    id: 3,
    name: "Surat Store",
    address: "Ring Road, Surat",
    coords: [72.8311, 21.1702], // Surat
  },
  {
    id: 4,
    name: "Vadodara Store",
    address: "Race Course Circle, Vadodara",
    coords: [73.1812, 22.3072], // Vadodara
  },
  {
    id: 5,
    name: "Valsad Store",
    address: "Station Road, Valsad",
    coords: [72.9342, 20.5992], // Valsad
  },
  {
    id: 6,
    name: "Bharuch Store",
    address: "Station Road, Bharuch",
    coords: [72.9781, 21.7051], // Bharuch
  },
  {
    id: 7,
    name: "Anand Store",
    address: "GIDCRoad, Anand",
    coords: [72.9581, 22.5645], // Anand
  },
  {
    id: 8,
    name: "Nadiad Store",
    address: "College Road, Nadiad",
    coords: [72.8619, 22.6939], // Nadiad
  },
  {
    id: 9,
    name: "Bhavnagar Store",
    address: "Waghawadi Road, Bhavnagar",
    coords: [72.1519, 21.7645], // Bhavnagar
  },
  {
    id: 10,
    name: "Jamnagar Store",
    address: "Teen Batti Circle, Jamnagar",
    coords: [70.0673, 22.4707], // Jamnagar
  },
];

export default function MapboxDistance({ distanceModal, setDistanceModal }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);

  const [startStore, setStartStore] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");

  const updateMarkers = (orderedStores = null) => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    if (!mapRef.current) return;

    if (!orderedStores) {
      LOCATIONS.forEach((loc) => {
        const el = document.createElement("div");
        el.style.backgroundColor = "#3b82f6";
        el.style.width = "24px";
        el.style.height = "24px";
        el.style.borderRadius = "50%";
        el.style.border = "2px solid white";
        el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(loc.coords)
          .setPopup(
            new mapboxgl.Popup().setHTML(
              `<strong>${loc.name}</strong><br>${loc.address}`
            )
          )
          .addTo(mapRef.current);
        markersRef.current.push(marker);
      });
    } else {
      orderedStores.forEach((loc, index) => {
        let color;
        if (index === 0) {
          color = "#22c55e";
        } else if (index === orderedStores.length - 1) {
          color = "#ef4444";
        } else {
          color = "#f59e0b";
        }

        const el = document.createElement("div");
        el.style.backgroundColor = color;
        el.style.width = "36px";
        el.style.height = "36px";
        el.style.borderRadius = "50%";
        el.style.border = "3px solid white";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.fontWeight = "bold";
        el.style.color = "white";
        el.style.fontSize = "14px";
        el.style.cursor = "pointer";
        el.style.boxShadow = "0 3px 10px rgba(0,0,0,0.4)";
        el.textContent = index + 1;

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(loc.coords)
          .setPopup(
            new mapboxgl.Popup().setHTML(
              `<strong>#${index + 1}: ${loc.name}</strong><br>${loc.address}`
            )
          )
          .addTo(mapRef.current);

        markersRef.current.push(marker);
      });
    }
  };

  useEffect(() => {
    if (!distanceModal || mapRef.current) return;

    const timer = setTimeout(() => {
      if (!mapContainerRef.current) return;

      try {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: "mapbox://styles/mapbox/streets-v11",
          center: [72.5, 22.3],
          zoom: 7,
        });

        mapRef.current.on("load", () => {
          console.log("Map loaded with 10 stores");
          updateMarkers();
        });
      } catch (error) {
        console.error("Map error:", error);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [distanceModal]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const fetchOptimizedRoute = async (startingStore) => {
    const otherStores = LOCATIONS.filter((loc) => loc.id !== startingStore.id);
    const allStores = [startingStore, ...otherStores];
    const coordinates = allStores.map((loc) => loc.coords.join(",")).join(";");

    console.log("Optimizing route for coordinates:", coordinates);

    const res = await fetch(
      `https://api.mapbox.com/optimized-trips/v1/mapbox/driving/${coordinates}?source=first&destination=last&roundtrip=false&geometries=geojson&overview=full&access_token=${mapboxgl.accessToken}`
    );

    const data = await res.json();
    console.log("Optimization API Response:", data);

    if (!data.trips || !data.trips[0]) {
      throw new Error("No optimized route found");
    }

    const trip = data.trips[0];
    const orderedStores = data.waypoints.map(
      (wp) => allStores[wp.waypoint_index]
    );

    return {
      orderedStores,
      geometry: trip.geometry,
      distance: trip.distance,
      duration: trip.duration,
    };
  };

  const fetchDetailedRoute = async (orderedStores) => {
    const coordinates = orderedStores
      .map((loc) => loc.coords.join(","))
      .join(";");

    const res = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?geometries=geojson&overview=full&steps=true&access_token=${mapboxgl.accessToken}`
    );

    const data = await res.json();
    console.log("Directions API Response:", data);

    if (!data.routes || !data.routes[0]) {
      throw new Error("No detailed route found");
    }

    const route = data.routes[0];

    const legs = route.legs.map((leg, i) => ({
      from: orderedStores[i].name,
      to: orderedStores[i + 1].name,
      distance: (leg.distance / 1000).toFixed(1),
      duration: (leg.duration / 60).toFixed(0),
    }));

    return {
      geometry: route.geometry,
      legs: legs,
      totalDistance: (route.distance / 1000).toFixed(1),
      totalDuration: (route.duration / 60).toFixed(0),
      totalDurationHours: (route.duration / 3600).toFixed(1),
    };
  };

  const calculateOptimizedRoute = async () => {
    if (!startStore) {
      alert("Please select a starting store");
      return;
    }

    setLoading(true);
    setRouteInfo(null);
    setLoadingStep("");

    try {
      setLoadingStep("🔄 Optimizing route for 10 stores...");
      console.log(`Starting from: ${startStore.name}`);

      const { orderedStores, geometry: optimizedGeometry } =
        await fetchOptimizedRoute(startStore);

      console.log("✓ Optimal order calculated:");
      orderedStores.forEach((s, i) => console.log(`  ${i + 1}. ${s.name}`));

      updateMarkers(orderedStores);
      drawRoute(optimizedGeometry);

      await new Promise((resolve) => setTimeout(resolve, 500));

      setLoadingStep("📊 Getting detailed leg-wise data...");
      const routeDetails = await fetchDetailedRoute(orderedStores);

      console.log("✓ Route details obtained");
      console.log(
        `Total: ${routeDetails.totalDistance} km, ${routeDetails.totalDurationHours} hours`
      );

      drawRoute(routeDetails.geometry);

      setRouteInfo({
        route: orderedStores.map((s) => s.name),
        legs: routeDetails.legs,
        totalDistance: routeDetails.totalDistance,
        totalDuration: routeDetails.totalDuration,
        totalDurationHours: routeDetails.totalDurationHours,
      });

      console.log("✓ Complete!");
    } catch (err) {
      console.error("Error:", err);
      alert(`Failed: ${err.message}`);
    } finally {
      setLoading(false);
      setLoadingStep("");
    }
  };

  const drawRoute = (geometry) => {
    if (!mapRef.current) return;

    if (!mapRef.current.isStyleLoaded()) {
      mapRef.current.once("styledata", () => {
        drawRoute(geometry);
      });
      return;
    }

    const routeData = {
      type: "Feature",
      properties: {},
      geometry: geometry,
    };

    try {
      if (mapRef.current.getLayer("route")) {
        mapRef.current.removeLayer("route");
      }
      if (mapRef.current.getSource("route")) {
        mapRef.current.removeSource("route");
      }

      mapRef.current.addSource("route", {
        type: "geojson",
        data: routeData,
      });

      mapRef.current.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#2563eb",
          "line-width": 5,
          "line-opacity": 0.85,
        },
      });

      console.log("✓ Route drawn on map");

      const coordinates = geometry.coordinates;
      if (coordinates && coordinates.length > 0) {
        const bounds = coordinates.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

        mapRef.current.fitBounds(bounds, {
          padding: { top: 100, bottom: 100, left: 100, right: 100 },
          duration: 1000,
        });
      }
    } catch (error) {
      console.error("Error drawing route:", error);
    }
  };

  const clearRoute = () => {
    setStartStore(null);
    setRouteInfo(null);
    updateMarkers();

    if (mapRef.current) {
      try {
        if (mapRef.current.getLayer("route")) {
          mapRef.current.removeLayer("route");
        }
        if (mapRef.current.getSource("route")) {
          mapRef.current.removeSource("route");
        }
      } catch (e) {
        console.log("Clear error:", e);
      }
    }
  };

  const handleClose = () => {
    clearRoute();
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    setDistanceModal(false);
  };

  if (!distanceModal) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          width: "95vw",
          height: "90vh",
          backgroundColor: "#fff",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
        }}
      >
        <div
          style={{
            width: "450px",
            minWidth: "450px",
            padding: "20px",
            overflowY: "auto",
            borderRight: "1px solid #e0e0e0",
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h2 style={{ fontSize: "22px", fontWeight: "bold", margin: 0 }}>
              10-Store Route Optimizer
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "28px",
                cursor: "pointer",
                padding: "0",
                lineHeight: "1",
              }}
            >
              ×
            </button>
          </div>

          <div
            style={{
              padding: "12px",
              backgroundColor: "#dbeafe",
              borderRadius: "8px",
              marginBottom: "16px",
              border: "1px solid #93c5fd",
            }}
          >
            <p
              style={{
                fontSize: "13px",
                color: "#1e40af",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              🎯 <strong>Auto-Optimization:</strong> Pick your starting store
              and we'll find the shortest route through all 10 stores in
              Gujarat!
            </p>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <h3
              style={{
                fontWeight: "600",
                marginBottom: "10px",
                fontSize: "15px",
                color: "#22c55e",
              }}
            >
              🟢 Starting Store
            </h3>
            <select
              value={startStore?.id || ""}
              onChange={(e) => {
                const store = LOCATIONS.find(
                  (loc) => loc.id === parseInt(e.target.value)
                );
                setStartStore(store || null);
                setRouteInfo(null);
              }}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "2px solid #22c55e",
                fontSize: "14px",
                backgroundColor: startStore ? "#f0fdf4" : "#fff",
                cursor: "pointer",
              }}
            >
              <option value="">Select starting point...</option>
              {LOCATIONS.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
            {startStore && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#15803d",
                  border: "1px solid #bbf7d0",
                }}
              >
                📍 {startStore.address}
              </div>
            )}
          </div>

          <button
            onClick={calculateOptimizedRoute}
            disabled={!startStore || loading}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: !startStore || loading ? "#d1d5db" : "#2563eb",
              color: "#fff",
              fontSize: "15px",
              fontWeight: "600",
              cursor: !startStore || loading ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              boxShadow:
                !startStore || loading
                  ? "none"
                  : "0 4px 12px rgba(37, 99, 235, 0.3)",
            }}
          >
            {loading ? loadingStep : "🚀 Calculate Best Route"}
          </button>

          {routeInfo && (
            <>
              <div
                style={{
                  marginTop: "16px",
                  backgroundColor: "#f0fdf4",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "2px solid #86efac",
                }}
              >
                <h3
                  style={{
                    fontWeight: "600",
                    marginBottom: "10px",
                    margin: "0 0 10px 0",
                    fontSize: "15px",
                    color: "#15803d",
                  }}
                >
                  🗺️ Optimized Route
                </h3>
                <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                  {routeInfo.route.map((name, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                        fontSize: "13px",
                      }}
                    >
                      <span
                        style={{
                          width: "26px",
                          height: "26px",
                          backgroundColor:
                            i === 0
                              ? "#22c55e"
                              : i === routeInfo.route.length - 1
                              ? "#ef4444"
                              : "#f59e0b",
                          color: "#fff",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "11px",
                          fontWeight: "bold",
                          marginRight: "8px",
                          flexShrink: 0,
                          border: "2px solid white",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }}
                      >
                        {i + 1}
                      </span>
                      <span style={{ flex: 1, fontWeight: "500" }}>{name}</span>
                      {i < routeInfo.route.length - 1 && (
                        <span style={{ marginLeft: "6px", color: "#999" }}>
                          →
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  marginTop: "14px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  padding: "16px",
                  border: "2px solid #e2e8f0",
                }}
              >
                <h3
                  style={{
                    fontWeight: "700",
                    fontSize: "16px",
                    marginBottom: "12px",
                    color: "#1e293b",
                    margin: "0 0 12px 0",
                  }}
                >
                  📊 Trip Summary
                </h3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    marginBottom: "14px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#dbeafe",
                      padding: "12px",
                      borderRadius: "8px",
                      textAlign: "center",
                      border: "1px solid #93c5fd",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#1e40af",
                        marginBottom: "4px",
                        fontWeight: "600",
                      }}
                    >
                      📏 DISTANCE
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#1e40af",
                      }}
                    >
                      {routeInfo.totalDistance}
                    </div>
                    <div style={{ fontSize: "11px", color: "#1e40af" }}>km</div>
                  </div>

                  <div
                    style={{
                      backgroundColor: "#dcfce7",
                      padding: "12px",
                      borderRadius: "8px",
                      textAlign: "center",
                      border: "1px solid #86efac",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#15803d",
                        marginBottom: "4px",
                        fontWeight: "600",
                      }}
                    >
                      ⏱️ TIME
                    </div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#15803d",
                      }}
                    >
                      {routeInfo.totalDurationHours}
                    </div>
                    <div style={{ fontSize: "11px", color: "#15803d" }}>
                      hours
                    </div>
                  </div>
                </div>

                <h4
                  style={{
                    fontWeight: "600",
                    marginBottom: "8px",
                    fontSize: "13px",
                    margin: "0 0 8px 0",
                    color: "#475569",
                  }}
                >
                  🛣️ Leg Details
                </h4>
                <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                  {routeInfo.legs.map((leg, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "8px",
                        marginBottom: "6px",
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        fontSize: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          marginBottom: "4px",
                          color: "#334155",
                        }}
                      >
                        #{i + 1}: {leg.from} → {leg.to}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        <span>📏 {leg.distance} km</span>
                        <span>⏱️ {leg.duration} min</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={clearRoute}
                style={{
                  width: "100%",
                  marginTop: "14px",
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#fff",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                🔄 Clear & Start Over
              </button>
            </>
          )}

          <div
            style={{
              marginTop: "16px",
              padding: "10px",
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              textAlign: "center",
              border: "1px solid #e5e7eb",
            }}
          >
            <span style={{ fontSize: "12px", color: "#6b7280" }}>
              🏪 <strong>10 Stores</strong> in Gujarat
            </span>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            position: "relative",
            minHeight: "100%",
            minWidth: 0,
          }}
        >
          <div
            ref={mapContainerRef}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </div>
      </div>
    </div>
  );
}
